import SourceFlowImage from "@sourceflow-uk/sourceflowimage";
import EditableImage from "@/components/EditableImage";
import EditableText from "@/components/EditableText";
import { Container, Nav, NavItem, Row, Col, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledDropdown } from "reactstrap";
import styles from "./styles.module.scss";
import clsx from 'classnames';
import Link from "next/link";
import Logo from "/public/site-assets/svg/logo-filled-black.svg";
import React, { useState, useEffect } from "react";
import NewNavigation from "../../.sourceflow/new_navigation.json";
import NewNavigationAU from "../../.sourceflow/new_navigation_au.json";
import NewNavigationUS from "../../.sourceflow/new_navigation_us.json";
import SearchIcon from "/public/site-assets/svg/black-magnifying-glass.svg";
import CloseMark from "/public/site-assets/svg/close-mark.svg";
import Telephone from "/public/site-assets/svg/telephone.svg";
import News from "../../.sourceflow/news.json";
import DynamicPages from "../../.sourceflow/dynamic_pages.json";
import Podcasts from "../../.sourceflow/podcasts.json";
import Fuse from "fuse.js";
import BaseObject from "@sourceflow-uk/sourceflow-sdk/base_object";
import global from "@/.sourceflow/global.json";
import { useRouter } from "next/router";
import { useLocale } from "@/hooks/useLocale";
import PoweredByBanner from "@/components/PoweredByBanner";

import UKFlag from "/public/site-assets/svg/uk-flag.svg";
import USFlag from "/public/site-assets/svg/us-flag.svg";
import AUFlag from "/public/site-assets/svg/au-flag.svg";

export default function MainHeader() {
  const router = useRouter();
  const currentPath = router.asPath.split(/[?#]/)[0].replace(/\/$/, "") || "/";
  // Homepages (UK and US) show the PoweredByBanner inside the header.
  const isHomepage = currentPath === "/" || currentPath === "/us";

  const dropdownRefs = {};

  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const toggleSearch = () => {
    setIsSearchOpen((prev) => {
      if (prev) {
        setSearchQuery("");
        setSearchResults([]);
      }
      return !prev;
    });
  };

  const AllInsights = News.category_values.map(v => ({
    ...v, href: `insights/${v.url_slug}`
  }));

  const usInsights = AllInsights.filter((insight) =>
    Array.isArray(insight.country.en)
      ? insight.country.en.includes("United States")
      : insight.country.en === "United States"
  );
  const ukInsights = AllInsights.filter((insight) =>
    Array.isArray(insight.country.en)
      ? insight.country.en.includes("United Kingdom")
      : insight.country.en === "United Kingdom"
  );

  // Static pages per region
  const ukStaticPages = [
    { title: "Home", url: "/" },
    { title: "About Us", url: "/about-us" },
    { title: "Meet the Team", url: "/about-us/meet-the-team" },
    { title: "Insights", url: "/insights" },
    { title: "Podcasts", url: "/podcasts" },
    { title: "Job Seekers", url: "/job-seekers" },
    { title: "Jobs", url: "/jobs" },
    { title: "Get Hired", url: "/get-hired" },
    { title: "Hire Now", url: "/hire-now" },
    { title: "Contact", url: "/contact" },
  ];

  const usStaticPages = [
    { title: "Home", url: "/us" },
    { title: "About Us", url: "/us/about-us" },
    { title: "Meet the Team", url: "/us/about-us/meet-the-team" },
    { title: "Insights", url: "/us/insights" },
    { title: "Podcasts", url: "/us/podcasts" },
    { title: "Job Seekers", url: "/us/job-seekers" },
    { title: "Jobs", url: "/us/jobs" },
    { title: "Get Hired", url: "/us/get-hired" },
    { title: "Contact Us", url: "/us/contact-us" },
  ];

  // Insight pages per region (normalized)
  const ukInsightPages = News.category_values
    .filter(v => Array.isArray(v.country.en) ? v.country.en.includes("United Kingdom") : v.country.en === "United Kingdom")
    .map(v => ({ title: v.title.en, url: `/insights/${v.url_slug}` }));

  const usInsightPages = News.category_values
    .filter(v => Array.isArray(v.country.en) ? v.country.en.includes("United States") : v.country.en === "United States")
    .map(v => ({ title: v.title.en, url: `/us/insights/${v.url_slug}` }));

  // Podcast pages per region
  const ukPodcastPages = Podcasts.category_values
    .filter(v => Array.isArray(v.country.en) ? v.country.en.includes("United Kingdom") : v.country.en === "United Kingdom")
    .map(v => ({ title: v.title.en, url: `/podcasts/${v.url_slug}` }));

  const usPodcastPages = Podcasts.category_values
    .filter(v => Array.isArray(v.country.en) ? v.country.en.includes("United States") : v.country.en === "United States")
    .map(v => ({ title: v.title.en, url: `/us/podcasts/${v.url_slug}` }));

  // Dynamic pages per region (normalized, with absolute URLs)
  // Strip common prefixes like "Specialisms - " or "Core Technology Expertise - "
  const cleanDynamicTitle = (title) => title.split(/ - ?/).pop().trim();

  const ukDynamicPages = DynamicPages
    .filter(v => !v.url_slug.startsWith('us/') && v.url_slug !== '/' && !v.is_draft)
    .map(v => ({ title: cleanDynamicTitle(v.title), url: `/${v.url_slug}` }));

  const usDynamicPages = DynamicPages
    .filter(v => v.url_slug.startsWith('us/') && !v.is_draft)
    .map(v => ({ title: cleanDynamicTitle(v.title), url: `/${v.url_slug}` }));

  // A page builder page and a collection item can share a URL — the page builder
  // page is the one that gets built, so it also replaces the collection item here
  // rather than both being listed. Static pages keep their hand-written labels.
  const dedupeByUrl = (pages) => {
    const seen = new Set();
    return pages.filter(({ url }) => {
      const key = url.replace(/\/$/, "").toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Combined search index per region
  const ukAllPages = dedupeByUrl([...ukStaticPages, ...ukDynamicPages, ...ukInsightPages, ...ukPodcastPages]);
  const usAllPages = dedupeByUrl([...usStaticPages, ...usDynamicPages, ...usInsightPages, ...usPodcastPages]);

  const isUsRoute = router.asPath.startsWith("/us"); // check if url is /us
  const isUkRoute = router.asPath.startsWith("/"); // check if url is /uk

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Fuse search instances per region
  const fuseOptions = {
    includeScore: true,
    keys: ["title"],
    threshold: 0.4,
  };

  const fuseUK = new Fuse(ukAllPages, fuseOptions);
  const fuseUS = new Fuse(usAllPages, fuseOptions);

  const handleSearch = (query) => {
    setSearchResults(query ? (isUsRoute ? fuseUS : fuseUK).search(query) : []);
  };

  // Add an event handler for the search input
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    handleSearch(query);
  };

  // ReciteMe Script
  useEffect(() => {
    const enableReciteButton = document.getElementById("reciteEnable");

    // Check if the loadService function exists
    if (typeof loadService !== "function") {
      return;
    }

    if (enableReciteButton) {
      enableReciteButton.addEventListener("click", loadService);
    }

    return () => {
      if (enableReciteButton) {
        enableReciteButton.removeEventListener("click", loadService);
      }
    };
  }, []);

  // New navigation
  const locale = useLocale();
  const main_global = new BaseObject(global, `${locale}`).toJson();

  let selectedNavigation;

  if (router.asPath.startsWith("/au")) {
    selectedNavigation = NewNavigationAU;
  } else if (router.asPath.startsWith("/us")) {
    selectedNavigation = NewNavigationUS;
  } else {
    selectedNavigation = NewNavigation;
  }

  // Now filter based on selectedNavigation
  const ParentMainNavigation = selectedNavigation.category_values.filter(post => post.parent.identifier === null);
  const ChildrenMainNavigation = selectedNavigation.category_values.filter(post => post.parent.identifier !== null);

  const [activeDropdown, setActiveDropdown] = useState(null);

  const newToggleDropdown = (titleEn) => {

    // Remove 'active' class from all elements
    document.querySelectorAll("[data-parent-id]").forEach((el) => {
      el.classList.remove("active");
    });
    
    setActiveDropdown(activeDropdown === titleEn ? null : titleEn);
    
    // Find the parent element with matching data-parent-id
    const parentElement = document.querySelector(`[data-parent-id="${titleEn}"]`);

    if (parentElement) {
      const isActive = parentElement.classList.contains("active");

      // Remove 'active' class from all elements
      document.querySelectorAll("[data-parent-id]").forEach((el) => {
        el.classList.remove("active");
      });

      // Toggle the clicked element only if it wasn't active before
      if (!isActive) {
        parentElement.classList.add("active");
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the clicked element is inside any data-parent-id element
      if (!event.target.closest("[data-parent-id]") && !event.target.closest(".dropdown") && !event.target.closest(".nav")) {
        setActiveDropdown(null);

        document.querySelectorAll("[data-parent-id]").forEach((el) => {
          el.classList.remove("active");
        });
      }
    };

    document.addEventListener("mouseover", handleClickOutside);
    return () => {
      document.removeEventListener("mouseover", handleClickOutside);
    };
  }, []);

  const [activeParent, setActiveParent] = useState(null);

  const handleParentClick = (parentTitle) => {
    setActiveParent((prev) => (prev === parentTitle ? null : parentTitle));
  };

  let selectedInsights;

  if (router.asPath.startsWith("/au")) {
    selectedInsights = ukInsights;
  } else if (router.asPath.startsWith("/us")) {
    selectedInsights = usInsights;
  } else {
    selectedInsights = ukInsights;
  }

  const featuredPost = selectedInsights
    ?.filter((post) => post?.featured_on_navigation?.en === true)
    .pop();

  let regionPath = "";
  if (router.asPath.startsWith("/au")) {
    regionPath = "au";
  } else if (router.asPath.startsWith("/us")) {
    regionPath = "us";
  }

  // END
  return (
    <>
      <section className={`${styles.root} main-navigation ${isOpen ? "active" : ""}`}>
        <div className="reciteWrapper py-2 bg-white">
          <Container>
            <button
              className="recite-btn p-0"
              id="reciteEnable"
              aria-label="Accessibility and Languages"
            >
              Accessibility and Languages
            </button>
          </Container>
        </div>
        {isHomepage && <PoweredByBanner />}
        <Container className="position-relative mainWrapper px-0 py-3 py-xl-0">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Link
                href={`${router.asPath.startsWith("/us") ? "/us" : "/"}`}
              >
                <a className="logo me-md-4" aria-label="Lorien" title="Lorien">
                  {/* SVGR strips the viewBox, which leaves the logo with no
                      coordinate system - a CSS width then crops it instead of
                      scaling it. Restoring the source viewBox lets the compact
                      band shrink it properly. */}
                  <Logo viewBox="0 0 160 70" />
                </a>
              </Link>
              <Nav className="d-none d-xl-flex">
                {ParentMainNavigation.map((post, index) => (
                  <NavItem key={index} className={`parent mx-1 m-xxxl-2 ${activeDropdown === post?.title.en ? "active" : ""}`}>
                    <button
                      className={`dropdown ${post.parent.identifier === null ? "has-children" : null
                        }`}
                      aria-label={`${post?.title.en}`}
                      onMouseEnter={() => newToggleDropdown(post?.title.en)}
                      ref={(ref) => (dropdownRefs[post?.title.en] = ref)}
                    >
                      {post?.title.en}
                    </button>
                  </NavItem>
                ))}
              </Nav>
            </div>
            <div className="d-flex align-items-center d-md-flex">
              <UncontrolledDropdown className="lang_select d-none d-xl-flex">
                <DropdownToggle aria-label="Language">
                  <span
                    className={
                      router.asPath.startsWith("/") &&
                        !router.asPath.startsWith("/us") &&
                        !router.asPath.startsWith("/au")
                        ? "active"
                        : ""
                    }
                  >
                    <UKFlag />
                  </span>
                  <span
                    className={
                      router.asPath.startsWith("/us") ? "active" : ""
                    }
                  >
                    <USFlag />
                  </span>
                  <span
                    className={
                      router.asPath.startsWith("/au") ? "active" : ""
                    }
                  >
                    <AUFlag />
                  </span>
                </DropdownToggle>

                <DropdownMenu aria-labelledby="dropdownMenuButton">
                  <DropdownItem
                    href="/"
                    title={`United Kingdom`}
                    aria-label={`United Kingdom`}
                    className={
                      router.asPath.startsWith("/") &&
                        !router.asPath.startsWith("/us") &&
                        !router.asPath.startsWith("/au")
                        ? "active"
                        : ""
                    }
                  >
                    <UKFlag />
                  </DropdownItem>
                  <DropdownItem
                    href="/us"
                    title={`United States`}
                    aria-label={`United States`}
                    className={
                      router.asPath.startsWith("/us") ? "active" : ""
                    }
                  >
                    <USFlag />
                  </DropdownItem>
                  <DropdownItem
                    href="/au"
                    title={`Austraila`}
                    aria-label={`United Austraila`}
                    className={
                      router.asPath.startsWith("/au") ? "active" : ""
                    }
                  >
                    <AUFlag />
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              <button
                className="search-icon d-flex d-none d-xl-inline-flex"
                aria-label="Search"
                onClick={toggleSearch}
              >
                <SearchIcon />
              </button>
              <Link
                href={`${router.asPath.startsWith("/us")
                  ? "/us/get-hired"
                  : "/get-hired"
                  }`}
              >
                <a
                  className="tertiaryBtn no-arrow text-nowrap m-0 d-none d-xl-inline-block ms-2"
                  aria-label="Get Hired"
                >
                  Get Hired
                </a>
              </Link>
              <Link
                href={`${router.asPath.startsWith("/us")
                  ? "/us/hire-now"
                  : "/hire-now"
                  }`}
              >
                <a
                  className="d-none d-xl-inline-block secondaryBtn no-arrow text-nowrap m-0 ms-2"
                  aria-label="Hire Now"
                >
                  Hire Now
                </a>
              </Link>
              <button
                aria-label="Menu"
                className={`d-inline-block d-xl-none mobile-menu ms-2 position-relative ${isOpen ? "active" : ""}`}
                onClick={() => setIsOpen((isOpen) => !isOpen)}
              >
                {!isOpen ? "Menu" : "Close"}
              </button>
            </div>
          </div>
        </Container>
        {ParentMainNavigation.length > 0 &&
          <div className="d-none d-xl-block">
            {ParentMainNavigation.map((parentPost, parentIndex) => {
              const childrenForParent = ChildrenMainNavigation.filter((childPost) => childPost.parent.id === parentPost.id);
              const parentLabel = parentPost?.title?.en || "Explore";
              // Skip rendering if no children exist
              if (childrenForParent.length === 0) {
                return null; // Exit early
              }
              return (
                <div
                  key={parentIndex}
                  className={clsx('dropdownItem')}
                  data-parent-id={parentPost.title.en}
                >
                  <div className="d-flex">
                    <div className="left-column col-4 p-4">
                      {(parentPost.title.en != "Insights") && (
                        <div>
                          <EditableImage
                            className={`mb-4 featured-image`}
                            img={{
                              path: `menu.featured_image.${parentIndex}.${regionPath}`,
                              src: `/site-assets/placeholders/img-placeholder.jpg`,
                            }}
                            size={"500x250"}
                            alt={``}
                          />
                          <EditableText
                            content={
                              <>
                                <h5>{parentLabel}</h5>
                                <p>Introduction to this section. Explore opportunities, expertise and updates tailored to this area.</p>
                              </>
                            }
                            path={`menu.featured_navigation.content.${parentIndex}.${regionPath}`}
                            global={main_global}
                            tag={`div`}
                            className={`mb-4 post-description`}
                            html={true}
                          />

                          {(parentPost.title.en === "About Us") && (
                            <Row className="mb-3 post-contact">
                              <Col md={6}>
                                <EditableText
                                  content={<a href="mailto:info@lorienglobal.com">info@lorienglobal.com</a>}
                                  path={`menu.featured_navigation.blurb.1.${parentIndex}.${regionPath}`}
                                  global={main_global}
                                  tag={`div`}
                                  className={``}
                                  html={true}
                                />
                              </Col>
                              <Col md={6}>
                                <EditableText
                                  content={<a href="tel:0207 654 1000">0207 654 1000</a>}
                                  path={`menu.featured_navigation.blurb.2.${parentIndex}.${regionPath}`}
                                  global={main_global}
                                  tag={`div`}
                                  className={``}
                                  type={"html"}
                                />
                              </Col>
                            </Row>
                          )}
                        </div>
                      )}
                      {parentPost.title.en === "Insights" && (
                        <div>
                          <EditableText
                            content={"Latest from Lorien"}
                            path={`menu.featured_navigation.content.${parentIndex}.${regionPath}`}
                            global={main_global}
                            tag={`h5`}
                            className={`mb-3 fw-bold text-white`}
                          />
                          {parentPost?.featured?.en ? (
                            <div>
                              <div className="featured-insights-image-wrapper position-relative">
                                <div>
                                  <a href={`${parentPost?.featured_url?.en}`}>
                                    <SourceFlowImage
                                      src={parentPost?.featured_image?.en}
                                      size="500x250"
                                      alt={`${parentPost.title.en}`}
                                      className={`mb-4 featured-image w-100`}
                                    />
                                  </a>
                                </div>

                              </div>
                            </div>
                          ) : featuredPost && (
                              <div>
                                <div className="featured-insights-image-wrapper position-relative">
                                  <div key={featuredPost?.id}>
                                    <a href={`${(regionPath && regionPath !== "au") ? `/${regionPath}` : ``}/insights/${featuredPost?.url_slug}`}>
                                      <SourceFlowImage
                                        src={featuredPost?.image?.en}
                                        size="500x250"
                                        alt={`${featuredPost?.title?.en}`}
                                        className={`mb-4 featured-image w-100`}
                                      />
                                    </a>
                                  </div>

                                </div>
                                <a href={`${(regionPath && regionPath !== "au") ? `/${regionPath}` : ``}/insights/${featuredPost?.url_slug}`}>
                                  <div className="mb-4 post-description post-blog">{featuredPost?.short_description?.en}</div>
                                </a>
                              </div>
                            )}
                        </div>
                      )}
                      <EditableText
                        content={`Discover ${parentLabel} Area`}
                        path={`menu.featured_navigation.button.${parentIndex}.${regionPath}`}
                        global={main_global}
                        html={true}
                        tag={`div`}
                        className={`primaryBtn-wrapper m-0 w-100 justify-content-center`}
                      />
                    </div>
                    <div className='right-column p-4 w-100'>
                      <div className="wrapper">
                        {ChildrenMainNavigation.filter((childPost) => childPost.parent.id === parentPost.id).map((childPost, childpostIndex) => (
                          <div className='child-nav' key={childPost.id}>
                            <div>
                              {childPost.new_tab?.en ?
                                <a className='sub-item-parent post-title' href={childPost.url.en} target="_blank" rel="noopener noreferrer">
                                  {childPost.title.en}
                                </a>
                                :
                                childPost.url.en === "#" ?
                                  <div className="sub-item-parent no-link post-title">{childPost.title.en}</div>
                                  :
                                  <a className='sub-item-parent post-title' href={childPost.url.en}>
                                    {childPost.title.en}
                                  </a>

                              }
                              {childPost.subtitle?.en && <div className='post-subtitle mb-4 mt-2'>{childPost.subtitle.en}</div>}
                            </div>
                            {ChildrenMainNavigation.filter((sub_childPost) => sub_childPost.parent.id === childPost.id).map((sub_childPost) => (
                              <div key={sub_childPost.id}>
                                {sub_childPost.new_tab?.en ?
                                  <a className='sub-item post-title' href={sub_childPost.url.en} target="_blank" rel="noopener noreferrer">
                                    {sub_childPost.title.en}
                                  </a> :
                                  <a className='sub-item post-title' href={sub_childPost.url.en}>
                                    {sub_childPost.title.en}
                                  </a>
                                }
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        }
      </section>
      <div className={`${styles.root} mobile-dropdown ${isHomepage ? "has-powered-by-banner" : ""} ${isOpen ? "active" : "overflow-hidden"} d-inline-block d-xl-none`}>
        <div className="d-flex flex-wrap justify-content-between mobileWrapper">
          <div className="w-100">
            {/* MOBILE */}
            <Nav className={`d-flex flex-wrap`}>
              <NavItem className={`parent w-100`}>
                <a href="/">
                  Home
                </a>
              </NavItem>
              {ParentMainNavigation.map((post, index) => {
                const childrenForParent = ChildrenMainNavigation.filter((childPost) => childPost.parent.id === post.id);
                const hasChildren = childrenForParent.length > 0;
                const isExpanded = activeParent === post.title.en;
                return (
                  <NavItem key={index} className={clsx("parent w-100", { expanded: isExpanded })}>
                    {hasChildren ? (
                      <button
                        className={clsx("bg-transparent dropdown w-100 text-start has-children", { open: isExpanded })}
                        aria-label={`${post?.title.en}`}
                        aria-expanded={isExpanded}
                        onClick={() => handleParentClick(post.title.en)}
                      >
                        <strong>{post?.title.en}</strong>
                      </button>
                    ) : (
                      <a href={post.url?.en ?? "#"}>
                        <strong>{post?.title.en}</strong>
                      </a>
                    )}

                    {hasChildren && (
                      <div className={clsx("sub-children-wrapper", { active: isExpanded })} aria-hidden={!isExpanded}>
                        <div
                          className={clsx("dropdownItem", { active: isExpanded })}
                          data-mobile-parent-id={post.title.en}
                        >
                          <div className="wrapper">
                            {childrenForParent.map((childPost) => (
                              <div className='child-nav' key={childPost.id}>
                                <div>
                                  {childPost.new_tab?.en ?
                                    <a href={childPost.url.en} target="_blank" rel="noopener noreferrer">
                                      {childPost.title.en}
                                    </a>
                                    :
                                    childPost.url.en === "#" ?
                                      <div className="sub-item-parent no-link">{childPost.title.en}</div>
                                      :
                                      <a href={childPost.url.en}>
                                        {childPost.title.en}
                                      </a>
                                  }
                                  {ChildrenMainNavigation.filter((sub_childPost) => sub_childPost.parent.id === childPost.id).map((sub_childPost) => (
                                    <div className="sub-item-child" key={sub_childPost.id}>
                                      {sub_childPost.new_tab?.en ?
                                        <a className='sub-item' href={sub_childPost.url.en} target="_blank" rel="noopener noreferrer">
                                          {sub_childPost.title.en}
                                        </a> :
                                        <a className='sub-item' href={sub_childPost.url.en}>
                                          {sub_childPost.title.en}
                                        </a>
                                      }
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                            {/* Same CTA copy the desktop mega-menu uses, so the
                                landing page stays reachable on mobile without
                                repeating the parent title. */}
                            <EditableText
                              content={`Discover ${post?.title?.en || "Explore"} Area`}
                              path={`menu.featured_navigation.button.${index}.${regionPath}`}
                              global={main_global}
                              html={true}
                              tag={`div`}
                              className={`primaryBtn-wrapper m-0 w-100 justify-content-center`}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </NavItem>
                );
              })}
            </Nav>
            <div className="mobile-get-hire-buttons">
              <Link
                href={`${router.asPath.startsWith("/us")
                  ? "/us/get-hired"
                  : "/get-hired"
                  }`}
              >
                <a
                  className="primaryBtn no-arrow text-nowrap m-0 mb-2 ms-2"
                  aria-label="Get Hired"
                  onClick={() => setIsOpen((isOpen) => !isOpen)}
                >
                  Get Hired
                </a>
              </Link>
              <Link
                href={`${router.asPath.startsWith("/us")
                  ? "/us/contact-us"
                  : "/hire-now"
                  }`}
              >
                <a
                  className="secondaryBtn no-arrow text-nowrap m-0 ms-2"
                  aria-label="Hire Now"
                  onClick={() => setIsOpen((isOpen) => !isOpen)}
                >
                  Hire Now
                </a>
              </Link>
            </div>
          </div>
          <div className="mobile-language w-100 d-flex">
            <div className="w-100 mb-4">
              <hr />
              <Container>
                <UncontrolledDropdown className="lang_select">
                  <DropdownToggle>
                    <span className={
                      router.asPath.startsWith("/") &&
                        !router.asPath.startsWith("/us") &&
                        !router.asPath.startsWith("/au")
                        ? "active"
                        : ""
                    }>
                      <UKFlag /> UK
                    </span>
                    <span className={router.asPath.startsWith("/us") ? "active" : ""}>
                      <USFlag /> US
                    </span>
                    <span className={router.asPath.startsWith("/au") ? "active" : ""}>
                      <AUFlag /> AU
                    </span>
                  </DropdownToggle>

                  <DropdownMenu aria-labelledby="dropdownMenuButton">
                    <DropdownItem
                      href="https://www.lorienglobal.com/"
                      title="United Kingdom"
                      className={router.asPath === "/" ? "active" : ""}
                    >
                      <UKFlag /> UK
                    </DropdownItem>
                    <DropdownItem
                      href="https://www.lorienglobal.com/us"
                      title="United States"
                      className={router.asPath.startsWith("/us") ? "active" : ""}
                    >
                      <USFlag /> US
                    </DropdownItem>
                    <DropdownItem
                      href="https://www.lorienglobal.com/au"
                      title="Australia"
                      className={router.asPath.startsWith("/au") ? "active" : ""}
                    >
                      <AUFlag /> AU
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </Container>
            </div>
          </div>
        </div>
      </div>
      {isSearchOpen && (
        <section className={`${styles.root} site-search d-none d-md-block`}>
          <Container>
            <div className="d-flex align-items-center">
              <div className="w-100 position-relative filters">
                <input
                  type="text"
                  id="search"
                  placeholder="Site Search..."
                  onChange={handleSearchChange}
                  value={searchQuery}
                />
                <span className="icon d-inline-block position-absolute">
                  <SearchIcon />
                </span>
              </div>
              <button
                className={`primaryBtn no-arrow border-0 m-0 ms-3`}
                aria-label="Search"
                onClick={() => toggleSearch()}
              >
                <CloseMark />
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-4 search-results">
                <p className="fs-4">
                  <strong>Search Results:</strong>
                </p>
                <ul>
                  {searchResults.map((result, index) => (
                    <li key={index}>
                      <a onClick={() => toggleSearch()} href={result.item.url}>
                        {result.item.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Container>
        </section>
      )}
    </>
  );
}
